import React, { useState, useEffect, useRef } from 'react';
import { Download, Globe, Shield, Code, Loader2, CheckCircle, AlertCircle, Trash2, Box, FileCode, Layers, Zap, Settings2 } from 'lucide-react';

// Load JSZip & FileSaver dari CDN
const loadScripts = () => {
  return new Promise((resolve) => {
    if (window.JSZip && window.saveAs) {
      resolve();
      return;
    }
    const jszip = document.createElement('script');
    jszip.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
    const fileSaver = document.createElement('script');
    fileSaver.src = 'https://cdnjs.cloudflare.com/ajax/libs/FileSaver.js/2.0.5/FileSaver.min.js';
    
    document.head.appendChild(jszip);
    document.head.appendChild(fileSaver);
    
    fileSaver.onload = () => resolve();
  });
};

const PROXY_SERVICES = [
  { id: 'auto', name: 'Auto-Rotate (Recommended)', url: null },
  { id: 'corsproxyio', name: 'CORS Proxy IO', url: (t) => `https://corsproxy.io/?${encodeURIComponent(t)}` },
  { id: 'codetabs', name: 'CodeTabs Proxy', url: (t) => `https://api.codetabs.com/v1/proxy?url=${encodeURIComponent(t)}` },
  { id: 'allorigins', name: 'AllOrigins Win', url: (t) => `https://api.allorigins.win/raw?url=${encodeURIComponent(t)}` },
  { id: 'thingproxy', name: 'ThingProxy', url: (t) => `https://thingproxy.freeboard.io/fetch/${encodeURIComponent(t)}` },
];

const App = () => {
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState('idle'); 
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [selectedProxy, setSelectedProxy] = useState('auto');
  const [proxyStatus, setProxyStatus] = useState({});
  
  const zipRef = useRef(null);
  const assetsDownloaded = useRef(new Set());

  useEffect(() => {
    loadScripts();
  }, []);

  const addLog = (msg, type = 'info') => {
    setLogs(prev => [`[${type.toUpperCase()}] ${msg}`, ...prev].slice(0, 150));
  };

  /**
   * Fungsi Fetch dengan Rotasi Proxy & Retry
   */
  const fetchWithProxyRotation = async (targetUrl, retries = 2) => {
    let optimizedUrl = targetUrl;
    if (targetUrl.includes('web.archive.org/web/') && !targetUrl.includes('id_')) {
      optimizedUrl = targetUrl.replace(/(\/web\/\d+)(\/)/, '$1id_$2');
    }

    const proxiesToTry = selectedProxy === 'auto' 
      ? PROXY_SERVICES.filter(p => p.id !== 'auto') 
      : PROXY_SERVICES.filter(p => p.id === selectedProxy);

    for (let proxy of proxiesToTry) {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          const finalUrl = proxy.url(optimizedUrl);
          const response = await fetch(finalUrl);
          
          if (response.ok) {
            setProxyStatus(prev => ({ ...prev, [proxy.id]: 'online' }));
            return response;
          }
          
          if (response.status === 404) throw new Error("File 404");
        } catch (err) {
          if (attempt === retries) {
            setProxyStatus(prev => ({ ...prev, [proxy.id]: 'error' }));
          } else {
            await new Promise(r => setTimeout(r, 400 * (attempt + 1)));
          }
        }
      }
      if (selectedProxy === 'auto') {
        addLog(`Proxy ${proxy.name} gagal, mencoba cadangan...`, 'warn');
      }
    }
    throw new Error("Semua jalur proxy gagal merespons.");
  };

  const processCssAssets = async (cssContent, baseUrl, zip) => {
    const urlPattern = /url\(['"]?([^'")]+)['"]?\)/g;
    let newContent = cssContent;
    const assetsFolder = zip.folder("assets/internal");
    const matches = [...cssContent.matchAll(urlPattern)];
    
    for (const match of matches) {
      const originalUrl = match[1];
      if (originalUrl.startsWith('data:') || originalUrl.startsWith('http')) continue;

      try {
        const absoluteUrl = new URL(originalUrl, baseUrl).href;
        const fileName = originalUrl.split('/').pop().split(/[?#]/)[0] || `asset_${Math.random().toString(36).substr(2, 5)}`;
        
        if (!assetsDownloaded.current.has(absoluteUrl)) {
          const res = await fetchWithProxyRotation(absoluteUrl);
          const blob = await res.blob();
          assetsFolder.file(fileName, blob);
          assetsDownloaded.current.add(absoluteUrl);
        }
        newContent = newContent.replaceAll(originalUrl, `../internal/${fileName}`);
      } catch (e) {
        addLog(`Aset CSS dilewati: ${originalUrl}`, 'warn');
      }
    }
    return newContent;
  };

  const startExtraction = async () => {
    if (!url.includes('web.archive.org/web/')) {
      setError('Gunakan URL lengkap Wayback (misal: web.archive.org/web/TIMESTAMP/URL)');
      return;
    }

    setStatus('loading');
    setLogs([]);
    setError('');
    assetsDownloaded.current.clear();
    setProxyStatus({});
    
    const zip = new window.JSZip();
    zipRef.current = zip;

    try {
      addLog("Mengunduh index halaman...");
      const response = await fetchWithProxyRotation(url);
      const htmlText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      addLog("Membersihkan injeksi Wayback...");
      const garbage = ['#wm-ipp-base', '#wm-ipp', '.wm-automated-backlink', 'script[src*="archive.org"]', 'link[href*="archive.org/static"]', '#wm-ipp-print', '#donato', 'iframe[src*="archive.org"]'];
      garbage.forEach(s => doc.querySelectorAll(s).forEach(el => el.remove()));

      const queue = [];
      doc.querySelectorAll('link[rel="stylesheet"]').forEach((link, i) => {
        const href = link.getAttribute('href');
        if (href) {
          const abs = new URL(href, url).href;
          const name = `style_${i}.css`;
          queue.push({ type: 'css', url: abs, name, el: link, attr: 'href', local: `./assets/css/${name}` });
        }
      });

      doc.querySelectorAll('script[src]').forEach((script, i) => {
        const src = script.getAttribute('src');
        if (src) {
          const abs = new URL(src, url).href;
          const name = `script_${i}.js`;
          queue.push({ type: 'js', url: abs, name, el: script, attr: 'src', local: `./assets/js/${name}` });
        }
      });

      doc.querySelectorAll('img, link[rel*="icon"], source').forEach((item, i) => {
        const src = item.getAttribute('src') || item.getAttribute('href') || item.getAttribute('srcset');
        if (src) {
          const abs = new URL(src.split(' ')[0], url).href;
          const ext = abs.split('.').pop().split(/[?#]/)[0] || 'png';
          const name = `media_${i}.${ext}`;
          const attr = item.tagName === 'IMG' || item.tagName === 'SOURCE' ? 'src' : 'href';
          queue.push({ type: 'img', url: abs, name, el: item, attr, local: `./assets/img/${name}` });
        }
      });

      setProgress({ current: 0, total: queue.length });
      setStatus('processing');

      for (let i = 0; i < queue.length; i++) {
        const task = queue[i];
        try {
          addLog(`Mendownload [${i+1}/${queue.length}]: ${task.name}`);
          const res = await fetchWithProxyRotation(task.url);
          
          if (task.type === 'css') {
            const cssText = await res.text();
            const cleanCss = await processCssAssets(cssText, task.url, zip);
            zip.folder("assets/css").file(task.name, cleanCss);
          } else {
            const blob = await res.blob();
            const folder = task.type === 'js' ? "assets/js" : "assets/img";
            zip.folder(folder).file(task.name, blob);
          }

          task.el.setAttribute(task.attr, task.local);
          assetsDownloaded.current.add(task.url);
        } catch (e) {
          addLog(`Gagal: ${task.name} (${e.message})`, 'error');
        }
        setProgress(prev => ({ ...prev, current: i + 1 }));
        await new Promise(r => setTimeout(r, 120));
      }

      zip.file("index.html", '<!DOCTYPE html>\n' + doc.documentElement.outerHTML);
      const content = await zip.generateAsync({ type: "blob" });
      window.saveAs(content, `template_${new Date().getTime()}.zip`);
      
      setStatus('done');
      addLog("Ekstraksi selesai dengan sukses!");
    } catch (err) {
      setError(err.message);
      setStatus('error');
      addLog(`Fatal: ${err.message}`, 'error');
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 font-sans p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-5">
            <div className="h-14 w-14 bg-gradient-to-tr from-blue-600 to-indigo-500 rounded-2xl flex items-center justify-center shadow-2xl shadow-blue-500/20">
              <Zap className="text-white" size={28} fill="white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-white tracking-tight">Wayback <span className="text-blue-500 font-extrabold italic underline decoration-blue-800">ULTRA</span></h1>
              <p className="text-slate-500 text-sm font-medium">Turbocharged Template Scraper & Localizer</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {PROXY_SERVICES.filter(p => p.id !== 'auto').map(p => (
              <div key={p.id} className={`px-3 py-1.5 rounded-full border text-[10px] font-bold uppercase tracking-wider flex items-center gap-2 transition-all ${
                proxyStatus[p.id] === 'online' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
                proxyStatus[p.id] === 'error' ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-slate-800 border-slate-700 text-slate-500'
              }`}>
                <div className={`h-1.5 w-1.5 rounded-full ${
                  proxyStatus[p.id] === 'online' ? 'bg-emerald-400' :
                  proxyStatus[p.id] === 'error' ? 'bg-red-400' : 'bg-slate-600'
                }`} />
                {p.name}
              </div>
            ))}
          </div>
        </div>

        {/* Input Card */}
        <div className="bg-[#0f172a] rounded-[2rem] border border-slate-800 p-6 md:p-10 shadow-3xl mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 block">URL Wayback Machine</label>
              <div className="relative group">
                <Globe className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-blue-500 transition-colors" size={20} />
                <input 
                  type="text" 
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Paste URL di sini (misal: https://web.archive.org/web/2010...)" 
                  className="w-full pl-14 pr-6 py-4 bg-[#1e293b]/40 border-2 border-slate-800 rounded-2xl focus:border-blue-500/50 transition-all outline-none text-white placeholder:text-slate-700 text-sm"
                />
              </div>
            </div>
            <div className="lg:col-span-4">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 block">Proxy Engine</label>
              <div className="relative">
                <Settings2 className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none" size={18} />
                <select 
                  value={selectedProxy}
                  onChange={(e) => setSelectedProxy(e.target.value)}
                  className="w-full pl-14 pr-6 py-4 bg-[#1e293b]/40 border-2 border-slate-800 rounded-2xl appearance-none outline-none focus:border-blue-500/50 text-white text-sm font-medium"
                >
                  {PROXY_SERVICES.map(p => <option key={p.id} value={p.id} className="bg-[#0f172a]">{p.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          
          <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex-1">
              {error && <p className="text-red-400 text-xs font-medium flex items-center gap-2"><AlertCircle size={14}/> {error}</p>}
              {!error && <p className="text-slate-500 text-xs italic">Aplikasi akan mengunduh HTML, CSS, JS, dan Gambar ke folder ZIP lokal.</p>}
            </div>
            <button 
              onClick={startExtraction}
              disabled={status === 'loading' || status === 'processing'}
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 text-white font-black px-12 py-4 rounded-2xl transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-3"
            >
              {status === 'processing' ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
              {status === 'processing' ? 'PROCESSING' : 'START TURBO EXTRACT'}
            </button>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Progress Card */}
          <div className="bg-[#0f172a] p-8 rounded-[2rem] border border-slate-800 flex flex-col justify-center text-center">
            <h3 className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mb-8">System Health & Progress</h3>
            
            <div className="relative inline-flex items-center justify-center mb-6">
              <svg className="w-32 h-32 transform -rotate-90">
                <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-800" />
                <circle 
                  cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="8" fill="transparent" 
                  strokeDasharray={364.42}
                  strokeDashoffset={364.42 - (364.42 * (progress.current / (progress.total || 1)))}
                  className="text-blue-500 transition-all duration-700 ease-out"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute flex flex-col">
                <span className="text-3xl font-black text-white">{Math.round((progress.current / (progress.total || 1)) * 100)}%</span>
              </div>
            </div>

            <p className="text-xs font-mono text-blue-400 mb-2 uppercase tracking-tighter">
              {progress.current} of {progress.total} Assets
            </p>
            
            {status === 'done' && (
              <div className="mt-4 py-2 bg-emerald-500/10 rounded-xl text-emerald-400 text-[10px] font-bold uppercase animate-pulse">
                Extraction Complete
              </div>
            )}
          </div>

          {/* Activity Log Terminal */}
          <div className="lg:col-span-2 bg-[#020617] rounded-[2rem] border border-slate-800 flex flex-col shadow-inner overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="h-2.5 w-2.5 bg-red-500/30 rounded-full" />
                  <div className="h-2.5 w-2.5 bg-yellow-500/30 rounded-full" />
                  <div className="h-2.5 w-2.5 bg-green-500/30 rounded-full" />
                </div>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-2">Console Live Stream</span>
              </div>
              <button onClick={() => setLogs([])} className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-600 hover:text-red-400 transition-all">
                <Trash2 size={14} />
              </button>
            </div>
            
            <div className="flex-1 p-6 h-[320px] overflow-y-auto font-mono text-[11px] leading-relaxed scrollbar-thin scrollbar-thumb-slate-800">
              {logs.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-800 font-bold italic tracking-tighter">
                  INITIALIZING_SYSTEM_BUFFER...
                </div>
              ) : (
                <div className="space-y-1.5">
                  {logs.map((log, i) => (
                    <div key={i} className={`p-1.5 rounded-lg border-l-2 transition-all ${
                      log.includes('ERROR') ? 'bg-red-500/5 border-red-500 text-red-400' : 
                      log.includes('WARN') ? 'bg-yellow-500/5 border-yellow-500 text-yellow-400' : 
                      'bg-blue-500/5 border-blue-600 text-blue-300'
                    }`}>
                      <span className="opacity-30 mr-2">➜</span> {log}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features Info */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 opacity-60 hover:opacity-100 transition-opacity">
           <div className="flex items-start gap-4 p-4 border border-slate-800 rounded-2xl">
              <Zap className="text-blue-500 flex-shrink-0" size={20} />
              <p className="text-[11px] text-slate-500"><strong className="text-slate-300 block mb-1">Raw Mode Extraction</strong> Secara otomatis menggunakan flag 'id_' untuk mengunduh konten asli tanpa modifikasi server archive.</p>
           </div>
           <div className="flex items-start gap-4 p-4 border border-slate-800 rounded-2xl">
              <Shield className="text-blue-500 flex-shrink-0" size={20} />
              <p className="text-[11px] text-slate-500"><strong className="text-slate-300 block mb-1">Advanced Clean-Up</strong> Mendeteksi dan menghapus puluhan skrip tracking dan toolbar CSS bawaan Wayback Machine.</p>
           </div>
           <div className="flex items-start gap-4 p-4 border border-slate-800 rounded-2xl">
              <Code className="text-blue-500 flex-shrink-0" size={20} />
              <p className="text-[11px] text-slate-500"><strong className="text-slate-300 block mb-1">CSS Deep Parsing</strong> Memindai file CSS untuk mengunduh background-image dan font-face secara lokal.</p>
           </div>
        </div>

      </div>
    </div>
  );
};

export default App;